import { FC, createContext, useContext, useMemo } from "react";
import {
  useQuery as reactQueryUseQuery,
  useMutation as reactQueryUseMutation,
  QueryClientProvider as ReactQueryQueryClientProvider,
  UseQueryResult as ReactQueryUseQueryResult,
  UseMutationResult as ReactQueryUseMutationResult,
  UseMutationOptions as ReactQueryUseMutationOptions,
  QueryClient as ReactQueryQueryClient,
} from "@tanstack/react-query";
import {
  IDataFetcherResponseError,
  IUseQueryContext,
  MutationKey,
  QueryClientProviderProps,
  QueryKey,
  UseQueryOptionsType,
} from "./types";
import { dataFetcher, getAbsoluteUrl, normalizeQueryKey } from "./utils";

const UseQueryContext = createContext<IUseQueryContext>({});

export function useQuery<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
>(
  queryKey: QueryKey,
  options: Omit<
    UseQueryOptionsType<TQueryFnData, TError, TData>,
    "queryKey" | "queryFn"
  > = {},
): ReactQueryUseQueryResult<TData, IDataFetcherResponseError<TError>> {
  const { baseUrl, onError } = useContext(UseQueryContext);

  return reactQueryUseQuery({
    queryKey: normalizeQueryKey(queryKey),
    refetchOnWindowFocus: false,
    async queryFn() {
      const { url, method, params } =
        typeof queryKey === "string"
          ? {
              url: queryKey,
              method: "GET",
              params: {},
            }
          : queryKey;

      const absoluteUrl = getAbsoluteUrl(url, { baseUrl, params });
      const { success, error, data } = await dataFetcher(absoluteUrl, {
        method,
        onError,
      });

      if (success) {
        return data;
      }

      throw error;
    },
    ...options,
  });
}

export function useMutation<
  TData = unknown,
  TError = unknown,
  TVariables = unknown,
  TContext = unknown,
>(
  mutationKey: MutationKey,
  options?: Omit<
    ReactQueryUseMutationOptions<
      TData,
      IDataFetcherResponseError<TError>,
      TVariables,
      TContext
    >,
    "mutationFn"
  >,
): ReactQueryUseMutationResult<
  TData,
  IDataFetcherResponseError<TError>,
  TVariables,
  TContext
> {
  const { baseUrl, onError } = useContext(UseQueryContext);

  return reactQueryUseMutation({
    async mutationFn(body: any) {
      const { url, method, params } = mutationKey;
      const absoluteUrl = getAbsoluteUrl(url, { baseUrl, params });
      const { success, data, error } = await dataFetcher(absoluteUrl, {
        method,
        body,
        onError,
      });

      if (success) {
        return data;
      }

      throw error;
    },
    ...options,
  });
}

const queryClient = new ReactQueryQueryClient({
  defaultOptions: {
    queries: {
      retry: 0,
    },
  },
});

export const QueryClientProvider: FC<QueryClientProviderProps> = ({
  baseUrl,
  onError,
  ...props
}) => {
  const contextValue = useMemo(() => {
    return {
      baseUrl,
      onError,
    };
  }, [baseUrl, onError]);

  return (
    <UseQueryContext.Provider value={contextValue}>
      <ReactQueryQueryClientProvider client={queryClient} {...props} />
    </UseQueryContext.Provider>
  );
};
